import React, { useState, Suspense, lazy, useMemo, useEffect } from "react";
import "../../css/rankTracking.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Spinner from "../../share/components/spinner.jsx";
import useQuery from "../../share/custom-hook/useQuery.js";
import SemButton from "../../../common/components/Button.jsx";
import { mergeNestedDataArrays } from "../../../helperJs/helper.js";
import { LLMTrackerStore } from "../../../store/LLMTrackerStore.js";
import { DELETE_LLM_PROJECT } from "../../../services/constants.jsx";
import LLMProjectList from "../../share/dropdown-menu/LLMProjectList.jsx";
import LLMProjectLocation from "../../share/dropdown-menu/LLMProjectLocation.jsx";
import AddProject from "../../share/components/add-project-section/llm-tracker/AddProject.jsx";

// icons
import DeleteIcon from "@mui/icons-material/Delete";
import GPTSVG from "../../Assets/rank-tracking-assets/gpt.svg";
import PPSVG from "../../Assets/rank-tracking-assets/pp.svg";
import CLAUDESVG from "../../Assets/rank-tracking-assets/claude.svg";
import { LuZap } from "react-icons/lu";

import FallbackLoader from "../../share/components/FallbackLoader.jsx";
import LLMShareOfVoice from "./LLMShareOfVoice.jsx";

const VisibilityTrends = lazy(() =>
  import("../../share/charts/llm-tracker/VisibiltyTrends.jsx")
);
const CopyLinkDialog = lazy(() =>
  import("../../../Layout/GuestLink/component/CopyLinkDialog.jsx")
);
const ProjectSetting = lazy(() => import("./ProjectSetting.jsx"));
const AverageChart = lazy(() =>
  import("../../share/charts/llm-tracker/AverageChart.jsx")
);
const LLMRankTable = lazy(() => import("./LLMRankTable.jsx"));
const AreYouSure = lazy(() => import("../../share/card-layout/AreYousure.jsx"));

const LLMTracking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location_path = useLocation();
  const isGuestLink = location_path.pathname === "/guest/llm-tracker";

  const [deleteAlert, setDeleteAlert] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(false);
  const { setQueryParams, removeQueryParams } = useQuery();

  const user_type = localStorage.getItem("user_type");
  const Is_organization = user_type === "organization";
  const { KEYWORD_LATEST_DATA, SHOWLOADER, SELECTED_PROJECT } = LLMTrackerStore(
    (state) => state
  );
  const { date, month, year } = KEYWORD_LATEST_DATA?.[0]?.data?.[0] || {};
  sessionStorage.setItem("LstUpdtRnkTckgTbl", `${month} ${date}, ${year}`);

  const handleAddQuery = () => {
    if (isGuestLink) return;
    setQueryParams({ domain: SELECTED_PROJECT?.weburl });
  };

  const handleRemoveQuery = () => {
    if (isGuestLink) return;
    removeQueryParams(["domain"]);
  };

  useEffect(() => {
    if (SELECTED_PROJECT?.weburl) {
      handleAddQuery();
    } else {
      handleRemoveQuery();
    }
  }, [SELECTED_PROJECT]);

  const rankGroups = useMemo(() => {
    const combineKeywordData = mergeNestedDataArrays(KEYWORD_LATEST_DATA);

    const initialGroups = {
      top3: { gpt: 0, pp: 0, cloude: 0 },
      top10: { gpt: 0, pp: 0, cloude: 0 },
      top100: { gpt: 0, pp: 0, cloude: 0 },
      noRank: { gpt: 0, pp: 0, cloude: 0 },
      improved: { gpt: 0, pp: 0, cloude: 0 },
      declined: { gpt: 0, pp: 0, cloude: 0 },
    };

    combineKeywordData?.forEach((res) => {
      // --- Existing Top/NoRank Logic ---

      // GPT
      const gptRank = Number(res.gpt_rank);
      const gptPrevRank = Number(res.gpt_prev_rank);
      const gptRankIsNum = !isNaN(gptRank);
      const gptPrevRankIsNum = !isNaN(gptPrevRank);
      const gptRankIsNoRank =
        res.gpt_rank === "No Rank" ||
        res.gpt_rank === undefined ||
        res.gpt_rank === null ||
        res.gpt_rank === "";
      const gptPrevRankIsNoRank =
        res.gpt_prev_rank === "No Rank" ||
        res.gpt_prev_rank === undefined ||
        res.gpt_prev_rank === null ||
        res.gpt_prev_rank === "";

      // Existing logic for top3, top10, top100, noRank
      if (gptRankIsNum) {
        if (gptRank <= 3) initialGroups.top3.gpt++;
        if (gptRank <= 10) initialGroups.top10.gpt++;
        if (gptRank <= 100) initialGroups.top100.gpt++;
      } else if (gptRankIsNoRank && res.gpt_content) {
        initialGroups.noRank.gpt++;
      }

      // Improved / Declined logic for GPT
      if (gptPrevRankIsNum && gptRankIsNum) {
        if (gptPrevRank > gptRank) initialGroups.improved.gpt++;
        else if (gptPrevRank < gptRank) initialGroups.declined.gpt++;
      } else if (gptPrevRankIsNoRank && gptRankIsNum) {
        // improvement from No Rank to ranked
        initialGroups.improved.gpt++;
      } else if (gptPrevRankIsNum && gptRankIsNoRank) {
        // decline from ranked to No Rank
        initialGroups.declined.gpt++;
      }

      // PP
      const ppRank = Number(res.pp_rank);
      const ppPrevRank = Number(res.pp_prev_rank);
      const ppRankIsNum = !isNaN(ppRank);
      const ppPrevRankIsNum = !isNaN(ppPrevRank);
      const ppRankIsNoRank =
        res.pp_rank === "No Rank" ||
        res.pp_rank === undefined ||
        res.pp_rank === null ||
        res.pp_rank === "";
      const ppPrevRankIsNoRank =
        res.pp_prev_rank === "No Rank" ||
        res.pp_prev_rank === undefined ||
        res.pp_prev_rank === null ||
        res.pp_prev_rank === "";

      if (ppRankIsNum) {
        if (ppRank <= 3) initialGroups.top3.pp++;
        if (ppRank <= 10) initialGroups.top10.pp++;
        if (ppRank <= 100) initialGroups.top100.pp++;
      } else if (ppRankIsNoRank && res.pp_content) {
        initialGroups.noRank.pp++;
      }

      if (ppPrevRankIsNum && ppRankIsNum) {
        if (ppPrevRank > ppRank) initialGroups.improved.pp++;
        else if (ppPrevRank < ppRank) initialGroups.declined.pp++;
      } else if (ppPrevRankIsNoRank && ppRankIsNum) {
        initialGroups.improved.pp++;
      } else if (ppPrevRankIsNum && ppRankIsNoRank) {
        initialGroups.declined.pp++;
      }

      // Claude
      const cloudeRank = Number(res.claude_rank);
      const cloudePrevRank = Number(res.claude_prev_rank);
      const cloudeRankIsNum = !isNaN(cloudeRank);
      const cloudePrevRankIsNum = !isNaN(cloudePrevRank);
      const cloudeRankIsNoRank =
        res.claude_rank === "No Rank" ||
        res.claude_rank === undefined ||
        res.claude_rank === null ||
        res.claude_rank === "";
      const cloudePrevRankIsNoRank =
        res.claude_prev_rank === "No Rank" ||
        res.claude_prev_rank === undefined ||
        res.claude_prev_rank === null ||
        res.claude_prev_rank === "";

      if (cloudeRankIsNum) {
        if (cloudeRank <= 3) initialGroups.top3.cloude++;
        if (cloudeRank <= 10) initialGroups.top10.cloude++;
        if (cloudeRank <= 100) initialGroups.top100.cloude++;
      } else if (cloudeRankIsNoRank && res.claude_content) {
        initialGroups.noRank.cloude++;
      }

      if (cloudePrevRankIsNum && cloudeRankIsNum) {
        if (cloudePrevRank > cloudeRank) initialGroups.improved.cloude++;
        else if (cloudePrevRank < cloudeRank) initialGroups.declined.cloude++;
      } else if (cloudePrevRankIsNoRank && cloudeRankIsNum) {
        initialGroups.improved.cloude++;
      } else if (cloudePrevRankIsNum && cloudeRankIsNoRank) {
        initialGroups.declined.cloude++;
      }
    });

    return initialGroups;
  }, [KEYWORD_LATEST_DATA]);

  useEffect(() => {
    LLMTrackerStore.setState({
      LLM_RANK_GROUP: rankGroups,
    });
  }, [rankGroups]);

  const keywordRankCardArrays = [
    {
      title: "Top 3",
      key: "top3",
      values: rankGroups.top3,
    },
    {
      title: "Top 10",
      key: "top10",
      values: rankGroups.top10,
    },
    {
      title: "Top 100",
      key: "top100",
      values: rankGroups.top100,
    },
    {
      title: "No Rank",
      key: "noRank",
      values: rankGroups.noRank,
    },
    {
      title: "Improved",
      key: "improved",
      values: rankGroups.improved,
    },
    {
      title: "Declined",
      key: "declined",
      values: rankGroups.declined,
    },
  ];

  const handleDeleteProject = async (res) => {
    if (!res) return setDeleteAlert(false);
    if (!SELECTED_PROJECT.id) return setDeleteAlert(false);
    try {
      const resp = await axios.delete(DELETE_LLM_PROJECT(SELECTED_PROJECT.id));
      sessionStorage.removeItem("LAST_LLMPRPJECT_SELECTED");
      LLMTrackerStore.setState({
        CURRLOCATION: {
          code: "All Locations",
          name: "All Locations",
        },
        SHOWLOADER: true,
        KEYWORD_LOC_DATA: "All Locations",
        KEYWORD_LATEST_DATA: [],
        KEYWORD_OLD_DATA: [],
        KEYWORD_ALL_LOC_DATA: [],
        USER_LLM_PROJECT: [],
        SELECTED_PROJECT: null,
      });
      setDeleteAlert(false);
    } catch (error) {
      setDeleteAlert(false);
    }
  };

  return (
    <main className="rank-tracking-container main-pd dashboard-app-container">
      <section className="project-selection">
        <div className="row p-0">
          <div className="col-lg-12 col-md-12">
            <div className="project-container">
              <div className="project-header">
                <h1 className="">LLM Rank Tracking For:</h1>
                {date ? (
                  <div className="project-last-updated">
                    {t("rank_tracking.map_rank_tracker.last_updated")}{" "}
                    <span>{`${month} ${date}`}</span>
                    <button
                      className="ms-3"
                      onClick={() => navigate("/llm-tracker/plans")}
                    >
                      <LuZap /> Upgrade
                    </button>
                  </div>
                ) : null}
              </div>
              <p>
                Select to get the detailed LLM Rank Tracking of your project
              </p>

              <div className="rank-project-sec">
                <div className="project-drop-down">
                  <div className="project-list">
                    <LLMProjectList />
                  </div>
                  <div className="project-location">
                    <LLMProjectLocation />
                  </div>
                </div>
                {location_path.pathname === "/llm-tracker" ? (
                  <>
                    <AddProject />
                    <Suspense fallback={null}>
                      <ProjectSetting />
                    </Suspense>
                    <SemButton
                      title={t(
                        "rank_tracking.map_rank_tracker.guest_link_button.tooltip"
                      )}
                      name={t(
                        "rank_tracking.map_rank_tracker.guest_link_button.title"
                      )}
                      onClick={() => setAnchorEl(true)}
                    />
                    {!Is_organization && (
                      <SemButton
                        title={t(
                          "rank_tracking.map_rank_tracker.delete_project_button.tooltip"
                        )}
                        name={t(
                          "rank_tracking.map_rank_tracker.delete_project_button.title"
                        )}
                        onClick={() => setDeleteAlert(true)}
                        icon={<DeleteIcon />}
                      />
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
      <>
        {SHOWLOADER ? (
          <div className="rank-loader">
            <Spinner />
          </div>
        ) : (
          <>
            <section className="domian-data-card container-scroll mb-4">
              {keywordRankCardArrays?.map((item) => {
                if (item.title === "Declined" && isGuestLink) return null;
                const { gpt, pp, cloude } = item.values;
                return (
                  <div key={item.title} className="domain-card">
                    <div className="d-flex flex-column gap-2">
                      <span>{item.title}</span>
                      <span className="gap-1">
                        <img src={GPTSVG} alt="gpt icons" />
                        <img src={CLAUDESVG} alt="claude icons" />
                        <img src={PPSVG} alt="pp icons" />
                      </span>
                      <span className="d-flex heading-small font-light-bold gap-3 align-items-center justify-content-center">
                        {`${gpt}`} {" / "}
                        {`${cloude}`} {" / "}
                        {`${pp}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </section>
            <Suspense fallback={<FallbackLoader />}>
              <div className="row">
                <div className="col-sm-12 col-md-5">
                  <LLMShareOfVoice />
                </div>
                <div className="col-sm-12 col-md-7">
                  <AverageChart />
                </div>
              </div>
              <VisibilityTrends />
              <LLMRankTable />
              <CopyLinkDialog
                {...{
                  anchorEl,
                  setAnchorEl,
                  pathname: "llm-tracker",
                }}
              />
            </Suspense>
          </>
        )}
      </>
      {deleteAlert && (
        <Suspense fallback={<FallbackLoader />}>
          <AreYouSure
            callback={handleDeleteProject}
            title={"You will not able to recover this Project!"}
          />
        </Suspense>
      )}
    </main>
  );
};

export default LLMTracking;
